import { useQueryClient } from '@tanstack/react-query'
import _ from 'lodash'
import { Link, useParams } from 'react-router'
import invariant from 'tiny-invariant'
import { match, P } from 'ts-pattern'
import { BeaconCancelButton } from '../../components/beacon/beacon-cancel-button.tsx'
import { BeaconRunReplay } from '../../components/beacon/beacon-run-replay.tsx'
import { RunSummaryStrip } from '../../components/beacon/beacon-run-summary-strip.tsx'
import { isRunFinished, pillFromRun, runProgressOf } from '../../components/beacon/beacon-run.ts'
import { BeaconScanningPill } from '../../components/beacon/beacon-scanning-pill.tsx'
import { BeaconLiveView } from '../../components/beacon/live/beacon-live-view.tsx'
import { useBeaconRun } from '../../components/beacon/use-beacon-run.ts'
import { Breadcrumb } from '../../components/breadcrumb.tsx'
import { ErrorBoundaryFallback } from '../../components/error-boundaries/error-boundary-fallback.tsx'
import { Spinner } from '../../components/spinner.tsx'
import { useRereadUntilSettled } from '../../hooks/use-reread-until-settled.ts'
import { Routes } from '../../routes.ts'
import {
  BEACON_RUNNING_RUN_POLL_MS,
  getBeaconRunQueryKey,
  getBeaconRunRepositoryId,
  invalidateBeaconRuns,
  invalidateGetScans,
  isBeaconRunNotFound,
  useGetBeaconRun,
  useGetRepositoryFromHalLink,
} from '../../utils/api-client/api-hooks.ts'
import type { BeaconRun } from '../../utils/api-client/api-schemas.ts'
import { formatDate } from '../../utils/date-utils.ts'
import { ErrorDetail } from '../../utils/error-handling/error-detail.tsx'
import { BeaconRunCompleted } from './beacon-run-completed.tsx'
import * as styles from './beacon-run-page.css.ts'

/**
 * One Beacon run, from the moment it is queued: waiting for a scanner, the live view while it runs, and
 * where it ended once it has.
 */
export const BeaconRunPage: React.FC = () => {
  const { runId } = useParams()
  invariant(runId, 'BeaconRunPage needs a runId route parameter')
  const { data: run, isLoading, error, refetch } = useGetBeaconRun({ runId })
  const { data: repository } = useGetRepositoryFromHalLink({ path: run?._links.repository.href })

  if (isLoading) {
    return (
      <div className={styles.loadingState}>
        <Spinner label="Loading Beacon run" />
      </div>
    )
  }

  if (!run) {
    return (
      <div className={styles.page}>
        {error && !isBeaconRunNotFound(error) ? (
          <ErrorBoundaryFallback
            title="Unable to load Beacon run"
            detail={<ErrorDetail error={error} />}
            onRetry={() => refetch()}
          />
        ) : (
          <>
            <h1 className={styles.title}>Beacon run not found</h1>
            <Link to={Routes.RepositoriesPage.createPath()}>Back to repositories</Link>
          </>
        )}
      </div>
    )
  }

  const repositoryName = repository?.name ?? run._links.repository.title
  const branch = run.branch ?? repository?.default_branch
  const repositoryId = getBeaconRunRepositoryId(run)

  return (
    <div className={styles.page}>
      <Breadcrumb
        navAriaLabel="Beacon run breadcrumb"
        items={[
          { label: 'All repositories', to: Routes.RepositoriesPage.path },
          {
            label: repositoryName ?? 'Repository',
            to: repositoryId ? Routes.RepositoryDetailsPage.createPath({ repositoryId }) : '#',
          },
          { label: 'Beacon run', to: '#' },
        ]}
      />
      <RunHeader run={run} repositoryName={repositoryName} branch={branch} />
      {match(run.state)
        .with('queued', 'dispatching', () => <BeaconRunWaiting run={run} />)
        .with('in-progress', () => <BeaconRunLive run={run} repositoryName={repositoryName} branch={branch} />)
        .with('failed', 'cancelled', () => <BeaconRunStopped run={run} />)
        .with('completed', () => <BeaconRunCompleted run={run} />)
        .exhaustive()}
    </div>
  )
}

const formatWhen = (iso: string) => formatDate({ date: iso, type: 'mm/dd/yy-time-with-zone' })

const RunHeader: React.FC<{ run: BeaconRun; repositoryName?: string; branch?: string }> = ({
  run,
  repositoryName,
  branch,
}) => (
  <header className={styles.header}>
    <p className={styles.eyebrow}>Pixee Beacon run</p>
    <h1 className={styles.title}>{repositoryName ?? 'Beacon run'}</h1>
    <dl className={styles.facts}>
      {branch && <Fact label="Branch" value={branch} />}
      <Fact label="Effort" value={_.capitalize(run.effort)} />
      {run.requested_by && <Fact label="Requested by" value={run.requested_by} />}
      <Fact label="Queued" value={formatWhen(run.queued_at)} />
    </dl>
  </header>
)

const Fact: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className={styles.fact}>
    <dt>{label}</dt>
    <dd className={styles.factValue}>{value}</dd>
  </div>
)

const waitingCopy = (run: BeaconRun) =>
  match(run)
    .with(
      { state: 'queued', retry_after: P.string },
      ({ retry_after }) => `Every Beacon scanner is busy. The platform tries again at ${formatWhen(retry_after)}.`
    )
    .with(
      { state: 'queued' },
      () => 'Beacon starts this run as soon as a scanner is free. You can leave this page; the run keeps its place.'
    )
    .otherwise(() => 'The platform is handing the run to Beacon. The live view opens once Beacon accepts it.')

/** A run the platform holds: queued, waiting for capacity, or being handed to analysis-service. */
const BeaconRunWaiting: React.FC<{ run: BeaconRun }> = ({ run }) => (
  <section className={styles.card} aria-label="Beacon run status">
    <div className={styles.cardRow}>
      <BeaconScanningPill pill={pillFromRun(run)} />
      <BeaconCancelButton runId={run.id} />
    </div>
    <p className={styles.cardText}>{waitingCopy(run)}</p>
  </section>
)

/** The live view of a running run. When its stream ends the platform is re-read, so the page settles. */
const BeaconRunLive: React.FC<{ run: BeaconRun; repositoryName?: string; branch?: string }> = ({
  run,
  repositoryName,
  branch,
}) => {
  const queryClient = useQueryClient()
  const { run: live, settledSeq } = useBeaconRun(run.id)
  const hasEnded = isRunFinished(live)

  // Once the stream ends the page re-reads the run until it reads as finished, then leaves the live view
  // for where the run ended.
  useRereadUntilSettled({
    isSettling: hasEnded,
    onSettle: () => {
      invalidateBeaconRuns(queryClient)
      invalidateGetScans(queryClient)
    },
    reread: () => queryClient.invalidateQueries({ queryKey: getBeaconRunQueryKey(run.id) }),
    maxMs: BEACON_RUNNING_RUN_POLL_MS,
  })

  return (
    <BeaconLiveView
      run={live}
      settledSeq={settledSeq}
      repositoryName={repositoryName}
      branch={branch}
      cancelControl={<BeaconCancelButton runId={run.id} />}
    />
  )
}

/** A run that failed or was cancelled: why, and what it did before it stopped. */
const BeaconRunStopped: React.FC<{ run: BeaconRun }> = ({ run }) => (
  <>
    <RunSummaryStrip run={run} />
    {run.state === 'failed' ? (
      <section className={styles.banner({ tone: 'failed' })} role="alert" aria-label="Run failed">
        <p className={styles.bannerTitle}>This run failed on {formatWhen(run.state_changed_at)}</p>
        <p className={styles.bannerText}>{run.failure_reason ?? 'Beacon did not report why.'}</p>
      </section>
    ) : (
      <section className={styles.banner({ tone: 'cancelled' })} aria-label="Run cancelled">
        <p className={styles.bannerTitle}>This run was cancelled on {formatWhen(run.state_changed_at)}</p>
        <p className={styles.bannerText}>Candidates it had not verified were not kept.</p>
      </section>
    )}
    {/* A run analysis-service never reported on has nothing to replay. */}
    {runProgressOf(run) && <BeaconRunReplay run={run} />}
  </>
)
